// API fetch wrapper for the admin portal.
// BASE resolves to VITE_API_BASE_URL env var or falls back to '/api'.

const BASE = import.meta.env['VITE_API_BASE_URL'] ?? '/api';

/** Read the csrf_staff cookie value (set by the API after login). */
export function getCsrfToken(): string {
  const match = /(?:^|;\s*)csrf_staff=([^;]+)/.exec(document.cookie);
  return match?.[1] ?? '';
}

export interface ApiFetchResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

/**
 * Thin fetch wrapper used throughout the admin portal.
 * - Always sends credentials (session cookie).
 * - Adds Content-Type: application/json.
 * - On mutations (non-GET/HEAD), adds X-CSRF-Token from the csrf_staff cookie.
 * - Never throws — catches network errors and returns {ok:false, status:0}.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<ApiFetchResult<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  const isMutation = method !== 'GET' && method !== 'HEAD';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (isMutation) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers,
    });

    let data: T;
    try {
      data = (await res.json()) as T;
    } catch {
      data = {} as T;
    }

    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: {} as T };
  }
}

/**
 * Fetch a path as a raw (non-JSON) response — e.g. a CSV export — and trigger
 * a browser download. Sends the session cookie. Throws on a non-2xx status so
 * callers can surface an error.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
